/**
 * Dev probe for packing-slip camera modal sizing.
 * Route: /shipping/dev/camera-capture-layout
 *
 * Open on the feature stack (gateway or /5196/ base). Read
 * `#camera-capture-layout-probe-json` or `data-camera-capture-layout-probe-ok`.
 * The layout fixture below uses expected pixel sizes without opening the camera.
 *
 * Pure math (no DOM): GET /api/dev/camera-capture-layout/probe with query params
 * pageContentWidth, viewportHeight, aspectRatio, deviceKind.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { CameraCaptureModal } from "@/components/ui/CameraCaptureModal";
import {
  buildCameraCaptureLayoutProbe,
  type CameraCaptureLayoutProbe,
} from "@/components/ui/cameraCaptureLayoutProbe";
import {
  CAMERA_PREVIEW_FALLBACK_ASPECT_PORTRAIT,
  readCameraCaptureDeviceKind,
  readCameraCaptureViewport,
} from "@/components/ui/cameraCaptureLayout";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { apiFetch } from "@/lib/api";
import { isFloorTabletDevice } from "@/lib/floorTabletDevice";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";

type ApiCameraCaptureLayoutProbe = {
  ok: boolean;
  inputs: {
    pageContentWidth: number;
    viewportHeight: number;
    aspectRatio: number;
    deviceKind: string;
  };
  expected: CameraCaptureLayoutProbe["expected"];
  checks: CameraCaptureLayoutProbe["checks"];
};

export function CameraCaptureLayoutTestPage(): JSX.Element {
  usePageBreadcrumb("Camera capture layout probe");

  const [cameraOpen, setCameraOpen] = useState(false);
  const [probe, setProbe] = useState<CameraCaptureLayoutProbe | null>(null);
  const [apiProbe, setApiProbe] = useState<ApiCameraCaptureLayoutProbe | null>(null);
  const [apiProbeError, setApiProbeError] = useState<string | null>(null);

  const touchCoarse =
    typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
  const touchHoverNone =
    typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;
  const viewport = readCameraCaptureViewport();
  const floorTablet = isFloorTabletDevice({
    coarsePointer: touchCoarse,
    hoverNone: touchHoverNone,
    viewportWidth: viewport.width,
  });
  const previewAspect = CAMERA_PREVIEW_FALLBACK_ASPECT_PORTRAIT;
  const wideTouchKiosk =
    (touchCoarse || touchHoverNone) && viewport.width > 1280;
  const deviceKind = readCameraCaptureDeviceKind({
    floorTablet,
    wideTouchKiosk,
    touchCoarse,
    touchHoverNone,
  });

  const runProbe = useCallback((): void => {
    setProbe(
      buildCameraCaptureLayoutProbe({
        modalOpen: cameraOpen,
        previewAspect,
        touchCoarse,
        touchHoverNone,
        floorTablet,
        hasHint: true,
      }),
    );
  }, [cameraOpen, floorTablet, previewAspect, touchCoarse, touchHoverNone]);

  const runApiProbe = useCallback(async (): Promise<void> => {
    setApiProbeError(null);
    try {
      const params = new URLSearchParams({
        pageContentWidth: String(probe?.pageContentWidth ?? 1200),
        viewportHeight: String(viewport.height),
        aspectRatio: String(previewAspect),
        deviceKind,
      });
      const result = await apiFetch<ApiCameraCaptureLayoutProbe>(
        `/api/dev/camera-capture-layout/probe?${params.toString()}`,
      );
      setApiProbe(result);
    } catch (error) {
      setApiProbe(null);
      setApiProbeError(error instanceof Error ? error.message : String(error));
    }
  }, [deviceKind, previewAspect, probe?.pageContentWidth, viewport.height]);

  useEffect(() => {
    runProbe();
    const onResize = (): void => runProbe();
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
    };
  }, [runProbe]);

  useEffect(() => {
    if (!cameraOpen) return;
    const timer = window.setInterval(() => runProbe(), 250);
    return () => window.clearInterval(timer);
  }, [cameraOpen, runProbe]);

  useEffect(() => {
    void runApiProbe();
  }, [runApiProbe]);

  const probeJson = useMemo(() => (probe == null ? "" : JSON.stringify(probe, null, 2)), [probe]);
  const apiProbeJson = useMemo(
    () => (apiProbe == null ? "" : JSON.stringify(apiProbe, null, 2)),
    [apiProbe],
  );
  const fixtureExpected = probe?.expected;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-lg font-semibold text-slate-900">Camera capture layout probe</h1>
          <p className="mt-1 text-sm text-slate-600">
            Uses the same hub <code className="text-xs">main</code> column width as Receive. The
            fixture below is sized to expected pixels so agents can verify layout without the
            webcam. Tolerance 4px on measured checks.
          </p>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => setCameraOpen(true)}>
              Open camera modal
            </Button>
            <Button type="button" variant="secondary" onClick={() => runProbe()}>
              Refresh probe
            </Button>
            <Button type="button" variant="secondary" onClick={() => void runApiProbe()}>
              Refresh API probe
            </Button>
          </div>
          <p className="text-sm font-medium text-slate-800">
            DOM status:{" "}
            <span className={probe?.ok ? "text-emerald-700" : "text-rose-700"}>
              {probe == null ? "pending" : probe.ok ? "ok" : "fail"}
            </span>
            {" · "}
            API status:{" "}
            <span className={apiProbe?.ok ? "text-emerald-700" : "text-rose-700"}>
              {apiProbeError != null
                ? "error"
                : apiProbe == null
                  ? "pending"
                  : apiProbe.ok
                    ? "ok"
                    : "fail"}
            </span>
          </p>
          {probe != null ? (
            <ul className="space-y-1 text-sm text-slate-700">
              {probe.checks.map((check) => (
                <li key={check.id} className={check.ok ? "text-emerald-800" : "text-rose-800"}>
                  {check.ok ? "ok" : "fail"} — {check.id}: {check.message}
                </li>
              ))}
            </ul>
          ) : null}
          {apiProbeError != null ? (
            <p className="text-sm text-rose-700">API probe error: {apiProbeError}</p>
          ) : null}
          <pre
            id="camera-capture-layout-probe-json"
            data-camera-capture-layout-probe-ok={probe?.ok === true ? "true" : "false"}
            className="max-h-[min(40vh,24rem)] overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800"
          >
            {probeJson}
          </pre>
          {apiProbeJson !== "" ? (
            <pre
              id="camera-capture-layout-api-probe-json"
              data-camera-capture-layout-api-probe-ok={apiProbe?.ok === true ? "true" : "false"}
              className="max-h-[min(30vh,18rem)] overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800"
            >
              {apiProbeJson}
            </pre>
          ) : null}
        </CardBody>
      </Card>

      {!cameraOpen && fixtureExpected != null ? (
        <div
          id="camera-capture-layout-fixture"
          data-camera-capture-layout-fixture=""
          data-camera-capture-layout-fixture-ok={probe?.ok === true ? "true" : "false"}
        >
          <h2 className="mb-2 text-sm font-semibold text-slate-800">Layout fixture (no camera)</h2>
          <Card
            data-camera-capture-probe-card=""
            className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-y-auto border border-dashed border-slate-300 bg-white shadow-sm"
            style={{
              width: `${String(fixtureExpected.cardWidth)}px`,
              maxHeight: "calc(100dvh - 2rem)",
            }}
          >
            <CardHeader className="shrink-0">
              <p className="text-sm font-medium text-slate-700">Expected modal card</p>
              <p className="mt-1 text-sm text-slate-600">
                Capture one page at a time. After you save, use Save and capture another for the next page.
              </p>
            </CardHeader>
            <CardBody className="flex flex-col gap-4">
              <div
                data-camera-capture-probe-preview=""
                className="mx-auto shrink-0 rounded-lg border border-slate-400 bg-slate-900"
                style={{
                  width: `${String(fixtureExpected.previewWidth)}px`,
                  height: `${String(fixtureExpected.previewHeight)}px`,
                  minWidth: `${String(fixtureExpected.previewWidth)}px`,
                  minHeight: `${String(fixtureExpected.previewHeight)}px`,
                }}
              />
            </CardBody>
          </Card>
        </div>
      ) : null}

      <CameraCaptureModal
        open={cameraOpen}
        title="Packing slip page"
        hint="Capture one page at a time. After you save, use Save and capture another for the next page."
        filenamePrefix="packing-slip-probe"
        onClose={() => setCameraOpen(false)}
        onCapture={() => {
          setCameraOpen(false);
          runProbe();
        }}
      />
    </div>
  );
}
